import { useMemo } from "react";
import type { z } from "zod";

import type { Prettify } from "@thalamos/common";
import type { WorkflowVersion } from "@thalamos/vision-common";
import {
  createInitialValues,
  getWorkflowTranslationContext,
  tk,
  workflows,
} from "@thalamos/vision-common";

import type { MultiPageFormProps } from "../../components/FormBuilder/MultiPageForm/MultiPageForm.js";
import {
  MultiPageForm,
  MultiPageFormSkeleton,
} from "../../components/FormBuilder/MultiPageForm/MultiPageForm.js";
import { Error } from "../../pages/Error/Error.js";

const SECTION_INDEX = 2;

type LeavePlaceOfSafetySchema<T extends WorkflowVersion> = z.infer<
  ReturnType<
    (typeof workflows)[T]["recordPlaceOfSafety"]["formDefinition"]["sections"][typeof SECTION_INDEX]["buildFormSpecification"]
  >["formSchema"]
>;

///

export function LeavePlaceOfSafetyFormSkeleton() {
  return <MultiPageFormSkeleton headerText={tk("leavePlaceOfSafety.title")} />;
}

///

export type LeavePlaceOfSafetyFormProps<T extends WorkflowVersion> = Prettify<
  {
    workflowVersion: T;
  } & Pick<
    MultiPageFormProps<LeavePlaceOfSafetySchema<T>>,
    | "initialValues"
    | "incidentWorkItem"
    | "onAutoSave"
    | "onSubmit"
    | "onCancel"
    | "onBack"
  >
>;

///

export function LeavePlaceOfSafetyForm<T extends WorkflowVersion>(
  props: LeavePlaceOfSafetyFormProps<T>,
) {
  const workflow = workflows[props.workflowVersion];
  const formSpecification =
    workflow?.recordPlaceOfSafety.formDefinition.sections[
      SECTION_INDEX
    ]?.buildFormSpecification({});

  const initialValuesOrDefaults: Record<string, unknown> = useMemo(
    () =>
      formSpecification === undefined
        ? {}
        : createInitialValues(
            formSpecification.strictFieldSchemas,
            props.initialValues,
          ),
    [props.initialValues, formSpecification],
  );

  if (formSpecification === undefined) {
    return <Error />;
  }

  const formSchema = formSpecification.formSchema;
  const strictFieldSchemas = formSpecification.strictFieldSchemas;

  return (
    <MultiPageForm<typeof formSchema>
      headerText={({ incidentWorkItem }) => ({
        translationKey: tk("leavePlaceOfSafety.title"),
        translationOptions: {
          context: getWorkflowTranslationContext({ incidentWorkItem }),
        },
      })}
      incidentWorkItem={props.incidentWorkItem}
      schema={formSchema}
      strictFieldSchemas={strictFieldSchemas}
      initialValues={initialValuesOrDefaults}
      pages={formSpecification.pages}
      onBack={props.onBack}
      onCancel={props.onCancel}
      onSubmit={props.onSubmit}
      onAutoSave={props.onAutoSave}
    />
  );
}
